import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, FileSearch, Scale, Search, Share2 } from 'lucide-react';
import { evictionRightsCountySlugs, toTitleCase } from '../../data/evictionRights2026';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
} from 'react-share';

const EvictionRightsDiscoverPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const filtered = useMemo(() => {
    const normalized = searchTerm.toLowerCase().trim();
    if (!normalized) return evictionRightsCountySlugs;
    return evictionRightsCountySlugs.filter((slug) => slug.includes(normalized));
  }, [searchTerm]);

  const canonicalUrl = 'https://rentsmart.ie/rights';
  const shareUrl = typeof window !== 'undefined' ? window.location.href : canonicalUrl;
  const shareText =
    'Can I be evicted in Ireland in 2026? Compare county eviction rules and tenant protections on RentSmart.ie.';

  return (
    <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden bg-slate-50">
      <Helmet>
        <title>Can I Be Evicted? Ireland Rental Rights Hub (2026)</title>
        <meta
          name="description"
          content="County-specific 2026 eviction rights pages: landlord tier checker, statutory notice periods, statutory declaration validator, and local legal aid contacts."
        />
        <meta
          name="keywords"
          content="can i be evicted ireland 2026, eviction rules dublin 2026, tmd tenancy rules ireland, small vs large landlord eviction rules"
        />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <header className="bg-slate-900 text-white pt-14 pb-16 px-4 relative overflow-hidden">
        <div className="absolute -top-12 -right-8 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-8 h-72 w-72 rounded-full bg-sky-500/20 blur-3xl" />

        <div className="relative max-w-7xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-widest mb-5">
            <AlertTriangle className="h-4 w-4" /> 2026 Tenancy Rights
          </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4">
            Can I Be Evicted From My Rental in 2026?
          </h1>
          <p className="text-slate-300 max-w-3xl text-lg leading-relaxed">
            County-level, data-driven rights pages for tenants and landlords at the exact moment an
            eviction notice lands.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="text-xs font-black uppercase tracking-widest text-slate-400">
              Share:
            </span>
            <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
              <FacebookIcon size={30} round />
            </FacebookShareButton>
            <TwitterShareButton url={shareUrl} title={shareText}>
              <TwitterIcon size={30} round />
            </TwitterShareButton>
            <WhatsappShareButton url={shareUrl} title={shareText}>
              <WhatsappIcon size={30} round />
            </WhatsappShareButton>
            <LinkedinShareButton
              url={shareUrl}
              title="Ireland Eviction Rights 2026"
              summary={shareText}
              source="RentSmart.ie"
            >
              <LinkedinIcon size={30} round />
            </LinkedinShareButton>
          </div>

          <div className="max-w-xl relative mt-8">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Find your county (e.g. dublin, cork, galway)"
              className="w-full rounded-xl bg-white text-slate-900 pl-12 pr-4 py-4 shadow-xl outline-none focus:ring-4 focus:ring-cyan-200"
            />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-10">
        <section className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <Scale className="h-5 w-5 text-emerald-600 mb-2" />
            <h2 className="text-sm font-black text-slate-900 mb-1">Landlord Tier Checker</h2>
            <p className="text-sm text-slate-600">
              Small vs large landlord logic with county-level context.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <FileSearch className="h-5 w-5 text-sky-600 mb-2" />
            <h2 className="text-sm font-black text-slate-900 mb-1">Notice Period Table</h2>
            <p className="text-sm text-slate-600">
              Statutory 2026 notice periods structured for fast legal checks.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <AlertTriangle className="h-5 w-5 text-amber-600 mb-2" />
            <h2 className="text-sm font-black text-slate-900 mb-1">Declaration Validator</h2>
            <p className="text-sm text-slate-600">
              Required documents and failure flags before any valid eviction process.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-black text-slate-900 mb-4">Choose County</h2>
          {filtered.length === 0 ? (
            <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500">
              No counties match your search.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {filtered.map((countySlug) => (
                <Link
                  key={countySlug}
                  to={`/rights/eviction-rules-${countySlug}-2026`}
                  className="no-underline rounded-2xl border border-slate-200 bg-white p-5 hover:-translate-y-1 hover:shadow-lg transition-all"
                >
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                    Eviction Rules 2026
                  </p>
                  <p className="text-xl font-black text-slate-900 mb-1">{toTitleCase(countySlug)}</p>
                  <p className="text-sm font-semibold text-slate-600 mb-4">
                    Can I be evicted in this county?
                  </p>
                  <p className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-wider text-emerald-700">
                    Open Guide <ArrowRight className="h-3 w-3" />
                  </p>
                </Link>
              ))}
            </div>
          )}
        </section>

        <section className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <p className="text-xs text-amber-900 font-semibold leading-relaxed">
            This hub provides informational guidance only and does not replace legal advice. Always
            verify final decisions using official RTB resources and qualified advisors.
          </p>
        </section>

        <section className="mt-4 rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-emerald-50 p-6">
          <h2 className="text-xl font-black text-slate-900 mb-2 flex items-center gap-2">
            <Share2 className="h-5 w-5 text-emerald-600" /> Share RentSmart.ie
          </h2>
          <p className="text-sm text-slate-600 font-medium mb-4 leading-relaxed">
            Help more renters and landlords discover county eviction rights guidance on RentSmart.ie.
          </p>
          <div className="flex items-center gap-2">
            <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
              <FacebookIcon size={32} round />
            </FacebookShareButton>
            <TwitterShareButton url={shareUrl} title={shareText}>
              <TwitterIcon size={32} round />
            </TwitterShareButton>
            <WhatsappShareButton url={shareUrl} title={shareText}>
              <WhatsappIcon size={32} round />
            </WhatsappShareButton>
            <LinkedinShareButton
              url={shareUrl}
              title="Ireland Eviction Rights 2026"
              summary={shareText}
              source="RentSmart.ie"
            >
              <LinkedinIcon size={32} round />
            </LinkedinShareButton>
          </div>
        </section>
      </main>
    </div>
  );
};

export default EvictionRightsDiscoverPage;
